import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

// Helper: JWT Decoding
function decodeJwt(token) {
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch { return null; }
}

function Homepage() {
  const navigate = useNavigate();
  const [info, setInfo] = useState({ name: "", email: "", role: "", organization: "" });
  const [stats, setStats] = useState({ projects: 12, photos: 450, annotations: 89 });

  useEffect(() => {
    const token = localStorage.getItem("token") || "";
    const nameLS = localStorage.getItem("name") || "";
    const orgLS = localStorage.getItem("userOrganization") || localStorage.getItem("organization") || "";
    let email = localStorage.getItem("rememberEmail") || "";
    
    const decoded = token ? decodeJwt(token) : null;
    setInfo({
      name: nameLS || (decoded?.email ? decoded.email.split("@")[0] : "User"),
      email: decoded?.email || "",
      role: decoded?.role || "Member",
      organization: orgLS || "Independent",
    });
  }, []);

  const logout = () => {
    localStorage.removeItem("token");
    window.location.href = "/";
  };

  return (
    <div className="vh-100 bg-light d-flex flex-column overflow-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>
      
      {/* HEADER: Fixed Light Design */}
      <nav className="navbar navbar-light bg-white border-bottom py-3 shadow-sm" style={{ zIndex: 10 }}>
        <div className="container">
          <span className="navbar-brand fw-bold mb-0 fs-4">
            PROJECT<span className="text-primary">MAP</span>
          </span>
          <div className="d-flex align-items-center gap-3">
            <span className="text-muted small pe-3 border-end border-light">
              <i className="bi bi-person-circle me-2 text-primary"></i>
              {info.name}
            </span>
            <button className="btn btn-outline-danger btn-sm px-3 fw-bold rounded-pill shadow-sm" onClick={logout}>
              Logout
            </button>
          </div>
        </div>
      </nav>

      {/* HERO SECTION: Balanced Padding to fill top space */}
      <header className="bg-white border-bottom py-4">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-7">
              <h1 className="fw-bold text-dark mb-1" style={{ fontSize: '2.5rem' }}>Welcome back, {info.name}!</h1>
              <p className="text-muted lead mb-0">
                Monitoring <span className="fw-bold text-dark">{info.organization}</span> geospatial data.
              </p>
            </div>
            <div className="col-lg-5">
              <div className="d-flex justify-content-lg-end gap-3">
                {[
                  { label: "Projects", value: stats.projects, color: "text-primary" },
                  { label: "Photos", value: stats.photos, color: "text-success" },
                  { label: "Notes", value: stats.annotations, color: "text-warning" }
                ].map((stat, idx) => (
                  <div key={idx} className="bg-white border rounded-3 shadow-sm px-4 py-2 text-center" style={{ minWidth: "110px" }}>
                    <h4 className={`fw-bold mb-0 ${stat.color}`}>{stat.value}</h4>
                    <span className="text-muted fw-bold small uppercase" style={{ fontSize: '10px' }}>{stat.label.toUpperCase()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* MAIN CONTENT: flex-grow-1 fills all remaining vertical space */}
      <main className="container flex-grow-1 py-4 overflow-hidden">
        <div className="row g-4 h-100">
          
          {/* LEFT: Quick Access - Height 100% */}
          <div className="col-lg-4 d-flex flex-column h-100">
            <h5 className="fw-bold mb-3">Quick Access</h5>
            <div className="card border-0 shadow-sm rounded-4 flex-grow-1 overflow-hidden">
              <div className="card-body p-4 d-flex flex-column justify-content-center text-center">
                <div className="bg-primary bg-opacity-10 text-primary rounded-4 p-4 mb-4 d-inline-block shadow-sm mx-auto">
                  <i className="bi bi-map-fill" style={{ fontSize: '3.5rem' }}></i>
                </div>
                <h3 className="fw-bold mb-3">Interactive Map</h3>
                <p className="text-muted px-2 mb-4">
                  Access real-time geospatial data, view 360° panoramas, and track project locations on a fully interactive interface.
                </p>
                <button 
                  onClick={() => navigate("/Project")} 
                  className="btn btn-primary btn-lg w-100 py-3 fw-bold rounded-3 shadow-sm border-0"
                >
                  <i className="bi bi-geo-fill me-2"></i> Open Map Explorer
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT: Recent Activity - Height 100% */}
          <div className="col-lg-8 d-flex flex-column h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0">Recent Activity</h5>
              <a href="/Projects" className="text-primary text-decoration-none fw-bold small">View All Projects →</a>
            </div>
            <div className="card border-0 shadow-sm rounded-4 flex-grow-1 overflow-hidden d-flex flex-column">
              <div className="table-responsive flex-grow-1 overflow-auto">
                <table className="table table-hover align-middle mb-0 h-100">
                  <thead className="bg-light sticky-top">
                    <tr className="small text-uppercase fw-bold text-muted border-0">
                      <th className="px-4 py-3 border-0 text-dark">Project Name</th>
                      <th className="border-0">Updated</th>
                      <th className="border-0 text-center">New Photos</th>
                      <th className="text-end px-4 border-0">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Data rows automatically scale based on available height */}
                    {[
                      { name: "Project Instance A", date: "Dec 17, 2025", count: 3 },
                      { name: "Project Instance B", date: "Dec 16, 2025", count: 6 },
                      { name: "Project Instance C", date: "Dec 15, 2025", count: 9 },
                      { name: "Project Instance D", date: "Dec 14, 2025", count: 12 },
                      { name: "Project Instance E", date: "Dec 13, 2025", count: 15 },
                    ].map((proj, i) => (
                      <tr key={i} style={{ cursor: "pointer" }} onClick={() => navigate(`/Project?projectId=${i}`)}>
                        <td className="px-4 py-4 fw-bold text-dark">{proj.name}</td>
                        <td className="text-muted">{proj.date}</td>
                        <td className="text-center">
                           <span className="badge bg-secondary rounded-pill px-3 py-2 fs-6 opacity-75">{proj.count}</span>
                        </td>
                        <td className="text-end px-4">
                          <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-3 py-2 fw-bold">ACTIVE</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>
      </main>

      {/* FOOTER: Static at bottom */}
      <footer className="py-3 bg-white border-top text-center mt-auto">
        <p className="text-muted small mb-0">
          <i className="bi bi-patch-check-fill text-primary me-2"></i>
          {info.organization} · Project Management System · 2025
        </p>
      </footer>
    </div>
  );
}

export default Homepage;